import { useStudentChapterMastery } from '../../../api/hooks';
import { Spinner, EmptyState, ErrorPanel, Button } from '../../../components/ui';
import { FaClipboardList } from '../../../components/ui/icons';
import Box from './Box';
import CompareBar from './CompareBar';

interface Props {
  studentId: number;
  /** First name, for the legend key. */
  studentName?: string;
  /** Jumps to the Performance tab (artboard "See full performance"). */
  onSeePerformance?: () => void;
}

export default function Top3WeakChapters({ studentId, studentName, onSeePerformance }: Props) {
  const q = useStudentChapterMastery(studentId, { subject: 'all' });
  if (q.isLoading) {
    return (
      <Box title="Weakest chapters" help="weakest_chapters">
        <div className="flex justify-center py-4"><Spinner size="md" /></div>
      </Box>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Box title="Weakest chapters" help="weakest_chapters"><ErrorPanel /></Box>
    );
  }
  const nameById: Record<number, string> = {};
  for (const c of q.data.chapters) nameById[c.id] = c.name;
  const classById: Record<number, number | null> = {};
  for (const a of q.data.class_avg_per_chapter ?? []) classById[a.chapter_id] = a.pct;
  const sorted = [...(q.data.student_cells ?? [])].sort((a, b) => a.pct - b.pct).slice(0, 3);
  const hasClass = sorted.some((c) => classById[c.chapter_id] != null);

  return (
    <Box title="Weakest chapters" help="weakest_chapters"
      actions={sorted.length > 0 ? (
        <span className="flex flex-wrap gap-3.5 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-accent" />{studentName || 'Student'}</span>
          {hasClass && <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-3 w-[3px] rounded-sm bg-navy dark:bg-ink" />Class</span>}
        </span>
      ) : undefined}>
      {sorted.length === 0 ? (
        <EmptyState compact icon={<FaClipboardList />} title="No chapter data yet" />
      ) : (
        <ul className="text-sm">
          {sorted.map((c) => {
            const name = nameById[c.chapter_id] ?? `Ch ${c.chapter_id}`;
            const classPct = classById[c.chapter_id] ?? null;
            return (
              <li key={c.chapter_id} className="py-2">
                <div className="mb-[7px] flex items-baseline justify-between gap-2.5 text-[13px]">
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink">{name}</span>
                  <span className="font-mono font-medium text-ink">
                    {c.pct.toFixed(0)}%
                    {classPct != null && <span className="text-muted"> / {classPct.toFixed(0)}%</span>}
                  </span>
                </div>
                <CompareBar fill="accent" value={c.pct} classValue={classPct} label={`${name}: student ${c.pct.toFixed(0)}%${classPct != null ? `, class ${classPct.toFixed(0)}%` : ''}`} />
              </li>
            );
          })}
        </ul>
      )}
      {onSeePerformance && (
        <Button variant="secondary" className="mt-3.5 w-full justify-center" onClick={onSeePerformance}>
          See full performance
        </Button>
      )}
    </Box>
  );
}
